import { Accessor, createSignal, JSX } from 'solid-js';
import {
  computePosition,
  flip,
  shift,
  offset,
  Placement,
} from '@floating-ui/dom';

const MENU_OFFSET = 8;

export interface UseFloatingMenuPositionOptions {
  getPosition?(): 'left' | 'right' | 'middle' | undefined;
  getAnchor(): HTMLElement | undefined;
}

export interface UseFloatingMenuPositionResult {
  readonly menuStyle: Accessor<JSX.CSSProperties>;
  readonly opensUpward: Accessor<boolean>;
  /** Ref callback for the floating menu section; seeds initial position then refines via rAF. */
  setMenuSectionRef(el: HTMLElement | null, onPositioned?: () => void): void;
}

/**
 * @intent Owns floating-ui positioning for a dropdown menu: seeds an initial
 * anchor-aligned position synchronously, then refines it on the next frame once
 * menu content has rendered. Shared by ControlDropdown and SplitControlDropdown.
 */
export const useFloatingMenuPosition = (
  options: UseFloatingMenuPositionOptions
): UseFloatingMenuPositionResult => {
  const [menuStyle, setMenuStyle] = createSignal<JSX.CSSProperties>({
    visibility: 'hidden',
  });
  const [opensUpward, setOpensUpward] = createSignal(false);
  let menuSectionRef: HTMLElement | undefined;

  const getPlacement = (): Placement => {
    const position = options.getPosition?.();
    if (position === 'right') return 'bottom-end';
    if (position === 'middle') return 'bottom';
    return 'bottom-start';
  };

  const updatePosition = async (): Promise<void> => {
    const anchorRef = options.getAnchor();
    if (!anchorRef || !menuSectionRef) return;
    const { x, y, placement } = await computePosition(
      anchorRef,
      menuSectionRef,
      {
        placement: getPlacement(),
        middleware: [
          offset(MENU_OFFSET),
          flip(),
          shift({ padding: MENU_OFFSET }),
        ],
      }
    );

    setMenuStyle({ top: `${y}px`, left: `${x}px`, visibility: 'visible' });
    setOpensUpward(placement.startsWith('top'));
  };

  const setMenuSectionRef = (
    el: HTMLElement | null,
    onPositioned?: () => void
  ): void => {
    if (!el) return;
    menuSectionRef = el;

    // Set initial position based on anchor before floating-ui calculates
    const anchorRef = options.getAnchor();
    if (anchorRef) {
      const rect = anchorRef.getBoundingClientRect();
      setMenuStyle({
        top: `${rect.bottom + MENU_OFFSET}px`,
        left: `${rect.left}px`,
        visibility: 'visible',
      });
    }

    // Wait for next frame so ControlMenu content has rendered, then refine position
    requestAnimationFrame(() => {
      void updatePosition().then(() => onPositioned?.());
    });
  };

  return { menuStyle, opensUpward, setMenuSectionRef };
};
